import React, { FunctionComponent, SyntheticEvent } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useForm, FormProvider } from 'react-hook-form';
import classnames from 'classnames';
import withStyles from 'react-jss';

import FormInput from 'components/FormComponents/FormInput';
import Button from 'components/Button';
import { ClassProp } from 'helpers/types';
import DisplayTimeStamp from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/DisplayTimestamp';
import {
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  OfferSummary,
  SetOfferLimitsFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import { SET_OFFER_LIMITS } from './SetOfferLimits.mutations';
import styles from './SetOfferLimits.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  offer: OfferSummary;
  cancelUpdate: (updateOffer: boolean) => void;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Limits Updated Successfully.',
  },
];

const SetOfferLimits: FunctionComponent<Props> = ({
  offer,
  cancelUpdate,
  classes,
}) => {
  const { offerUuid, offerStartDate, offerEndDate } = offer;

  const methods = useForm<SetOfferLimitsFormData>({
    mode: 'onBlur',
    defaultValues: {
      offerUuid,
      cashbackPercentageLimit: null,
      minimumSpendToActivate: null,
    },
  });

  const { register, handleSubmit, formState } = methods;

  const handleSetOfferLimitsResponse = () => {
    handleSuccess(defaultSuccessMessage);
    cancelUpdate(false);
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    SET_OFFER_LIMITS,
    {
      onCompleted: handleSetOfferLimitsResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) =>
    cancelUpdate(false);

  const onSubmit = ({
    offerUuid,
    cashbackPercentageLimit,
    minimumSpendToActivate,
  }: SetOfferLimitsFormData) => {
    const properties = {
      offerUuid,
      cashbackPercentageLimit,
      minimumSpendToActivate,
    };

    runMutation({
      variables: {
        params: { ...properties },
      },
    });
  };

  return (
    <Container className={classes.container}>
      <Header as="h2">Update Offer</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={offerUuid}
            name="offerUuid"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <DisplayTimeStamp label="Offer Start:" date={offerStartDate} />
              </Grid.Column>
              <Grid.Column width={8}>
                <DisplayTimeStamp label="Offer End:" date={offerEndDate} />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={6}>
                <FormInput
                  name="cashbackPercentageLimit"
                  type="text"
                  placeholder="Cashback Limit"
                  label="Cashback Limit"
                  register={register({ required: true })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={6}>
                <FormInput
                  name="minimumSpendToActivate"
                  type="text"
                  placeholder="Minimum Spend To Activate"
                  label="Minimum Spend To Activate"
                  register={register({
                    min: {
                      value: 0.01,
                      message: 'Minimun spend is 1 cent',
                    },
                  })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={isSubmitting || !formState.isValid}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Update Offer Limits'}
              </Button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(SetOfferLimits);
